import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Trophy, Medal, Award, Clock, ShieldAlert, Truck, Sparkles } from "lucide-react";

const ICONS = {
  on_time_timesheets: Clock,
  safety_reports: ShieldAlert,
  clean_pre_starts: Truck,
};
const ACCENTS = {
  on_time_timesheets: "bg-[#FF5F15]",
  safety_reports: "bg-red-600",
  clean_pre_starts: "bg-blue-600",
};

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/leaderboard/week")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="px-4 py-4 md:p-6 lg:p-10 max-w-5xl mx-auto pb-24 lg:pb-10" data-testid="leaderboard-page">
      <PageHeader
        tag="/ this week's numbers"
        title="The boards"
        subtitle={data ? `${formatDate(data.week_start)} → ${formatDate(data.week_end)} · resets every pay week` : "Loading…"}
      />

      <div className="hard-card border-l-4 border-l-[#FF5F15] p-4 mb-6 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-[#FF5F15] shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-sm">Three boards. All things you control.</div>
          <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
            No surveillance, no productivity hours. Just rewards for being on top of your gear, your safety and your paperwork.
          </p>
        </div>
      </div>

      {loading && <div className="hard-card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {data?.metrics?.map((m) => (
        <Board key={m.key} metric={m} youId={user.id} />
      ))}
    </div>
  );
}

function Board({ metric, youId }) {
  const Icon = ICONS[metric.key] || Trophy;
  const accent = ACCENTS[metric.key] || "bg-zinc-900";
  return (
    <div className="hard-card mb-4 overflow-hidden" data-testid={`leaderboard-board-${metric.key}`}>
      <div className="flex items-start gap-3 p-4 border-b border-zinc-200">
        <div className={`w-10 h-10 ${accent} text-white flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-bold uppercase tracking-tight text-lg leading-tight">{metric.label}</h3>
          <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{metric.blurb}</p>
        </div>
      </div>

      <div className="divide-y divide-zinc-100">
        {metric.top.length === 0 || metric.top[0].score === 0 ? (
          <div className="p-6 text-center text-xs text-zinc-500">Nobody on the board yet — be the first this week.</div>
        ) : (
          metric.top.filter((r) => r.score > 0).map((row) => (
            <Row key={row.user_id} row={row} isYou={row.user_id === youId} />
          ))
        )}
      </div>

      {/* "You" footer if not top 5 */}
      {metric.you && metric.you.user_id !== youId ? null :
        metric.you && metric.you.rank && metric.you.rank > 5 ? (
          <div className="bg-orange-50 border-t border-[#FF5F15] p-3" data-testid={`leaderboard-you-${metric.key}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#FF5F15] mb-1">Your rank</div>
            <Row row={metric.you} isYou />
          </div>
        ) : metric.you && metric.you.score === 0 ? (
          <div className="bg-zinc-50 border-t border-zinc-200 p-3 text-center text-[11px] text-zinc-500" data-testid={`leaderboard-you-${metric.key}`}>
            You&apos;re not on this board yet. {metric.total_participants > 0 ? `${metric.total_participants} mate${metric.total_participants === 1 ? "" : "s"} ahead.` : "Be the first."}
          </div>
        ) : null
      }
    </div>
  );
}

function Row({ row, isYou }) {
  const rankBadge = row.rank === 1 ? <Trophy className="w-4 h-4 text-yellow-500" /> :
                    row.rank === 2 ? <Medal className="w-4 h-4 text-zinc-400" /> :
                    row.rank === 3 ? <Award className="w-4 h-4 text-orange-700" /> :
                    <span className="text-[10px] font-mono font-bold text-zinc-400 w-4 text-center">{row.rank}</span>;
  const initials = (row.user_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className={`flex items-center gap-3 p-3 ${isYou ? "bg-orange-50" : ""}`} data-testid={`leaderboard-row-${row.user_id}`}>
      <div className="w-6 flex justify-center shrink-0">{rankBadge}</div>
      <div className="w-8 h-8 bg-zinc-900 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">
          {row.user_name}
          {isYou && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest bg-[#FF5F15] text-white px-1 py-0.5">YOU</span>}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{row.role}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display font-black text-2xl leading-none">{row.score}</div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}
